/**
 * Accounts Page
 * View vendor accounts with invoices, payments, and balances
 */

import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { subscribeToVendorAccounts, getVendorExpenses } from '../services/accountService';
import { formatCurrency, formatDate } from '../utils/formatters';
import './Accounts.css';

const Accounts = () => {
  const user = useAuthStore((state) => state.user);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorExpenses, setVendorExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);

  useEffect(() => {
    if (!user) return;

    setLoading(true);
    const unsubscribe = subscribeToVendorAccounts(user.uid, (vendorList) => {
      setVendors(vendorList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const handleVendorClick = async (vendor) => {
    setSelectedVendor(vendor);
    setLoadingExpenses(true);

    try {
      const expenses = await getVendorExpenses(user.uid, vendor.name);
      setVendorExpenses(expenses);
    } catch (error) {
      console.error('Error loading vendor expenses:', error);
    } finally {
      setLoadingExpenses(false);
    }
  };

  const handleBackClick = () => {
    setSelectedVendor(null);
    setVendorExpenses([]);
  };

  // Calculate totals
  const totalInvoiced = vendors.reduce((sum, v) => sum + v.totalAmount, 0);
  const totalPaid = vendors.reduce((sum, v) => sum + v.totalPaid, 0);
  const totalBalance = vendors.reduce((sum, v) => sum + v.balance, 0);

  if (loading) {
    return <div className="loading-state">Loading accounts...</div>;
  }

  // Show vendor detail view
  if (selectedVendor) {
    return (
      <div className="accounts-page">
        <div className="page-header">
          <div className="header-with-back">
            <button className="btn btn-back" onClick={handleBackClick}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back to Accounts
            </button>
            <div>
              <h1>{selectedVendor.name}</h1>
              <p className="page-subtitle">Account transactions and balance</p>
            </div>
          </div>
        </div>

        {/* Vendor Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#dbeafe' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Invoiced</div>
              <div className="stat-value">{formatCurrency(selectedVendor.totalAmount)}</div>
              <div className="stat-meta">{selectedVendor.expenseCount} transactions</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#dcfce7' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Paid</div>
              <div className="stat-value">{formatCurrency(selectedVendor.totalPaid)}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: selectedVendor.balance > 0 ? '#fef3c7' : '#dcfce7' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke={selectedVendor.balance > 0 ? '#f59e0b' : '#22c55e'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Balance</div>
              <div className="stat-value">{formatCurrency(selectedVendor.balance)}</div>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="card">
          <h2 className="card-title">Transactions</h2>
          {loadingExpenses ? (
            <div className="loading-state">Loading transactions...</div>
          ) : vendorExpenses.length === 0 ? (
            <div className="empty-state">
              <p>No transactions found for this vendor</p>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th className="text-right">Amount</th>
                  <th className="text-right">Payment</th>
                  <th className="text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {vendorExpenses.map((expense) => (
                  <tr key={expense.id}>
                    <td>
                      <div className="invoice-cell">
                        <span className="invoice-date">{formatDate(expense.date)}</span>
                        <span className="invoice-desc">{expense.description || expense.category}</span>
                      </div>
                    </td>
                    <td className="text-right amount-cell">{formatCurrency(expense.amount)}</td>
                    <td className="text-right payment-cell">
                      {expense.paid > 0 ? (
                        <span className="payment-amount">{formatCurrency(expense.paid)}</span>
                      ) : (
                        <span className="payment-pending">-</span>
                      )}
                    </td>
                    <td className="text-right balance-cell">
                      <span className={expense.balance > 0 ? 'balance-outstanding' : 'balance-settled'}>
                        {formatCurrency(expense.balance)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td><strong>Total</strong></td>
                  <td className="text-right"><strong>{formatCurrency(selectedVendor.totalAmount)}</strong></td>
                  <td className="text-right"><strong>{formatCurrency(selectedVendor.totalPaid)}</strong></td>
                  <td className="text-right">
                    <strong className={selectedVendor.balance > 0 ? 'balance-outstanding' : 'balance-settled'}>
                      {formatCurrency(selectedVendor.balance)}
                    </strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    );
  }

  // Show vendor list
  return (
    <div className="accounts-page">
      <div className="page-header">
        <div>
          <h1>Accounts</h1>
          <p className="page-subtitle">View vendor accounts and balances</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#dbeafe' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Vendors</div>
            <div className="stat-value">{vendors.length}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fee2e2' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Invoiced</div>
            <div className="stat-value">{formatCurrency(totalInvoiced)}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#dcfce7' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Paid</div>
            <div className="stat-value">{formatCurrency(totalPaid)}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: totalBalance > 0 ? '#fef3c7' : '#dcfce7' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke={totalBalance > 0 ? '#f59e0b' : '#22c55e'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Outstanding Balance</div>
            <div className="stat-value">{formatCurrency(totalBalance)}</div>
          </div>
        </div>
      </div>

      {/* Vendor List */}
      <div className="card">
        {vendors.length === 0 ? (
          <div className="empty-state">
            <p>No vendor accounts found</p>
            <p className="empty-hint">Add expenses with vendor names to see them here</p>
          </div>
        ) : (
          <div className="vendor-list">
            {vendors.map((vendor) => (
              <div
                key={vendor.name}
                className="vendor-card"
                onClick={() => handleVendorClick(vendor)}
              >
                <div className="vendor-info">
                  <div className="vendor-avatar">
                    {vendor.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="vendor-details">
                    <h3 className="vendor-name">{vendor.name}</h3>
                    <span className="vendor-count">{vendor.expenseCount} transaction{vendor.expenseCount !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                <div className="vendor-amounts">
                  <div className="vendor-total">
                    <span className="amount-label">Total</span>
                    <span className="amount-value">{formatCurrency(vendor.totalAmount)}</span>
                  </div>
                  <div className="vendor-balance">
                    <span className="amount-label">Balance</span>
                    <span className={`amount-value ${vendor.balance > 0 ? 'balance-outstanding' : 'balance-settled'}`}>
                      {formatCurrency(vendor.balance)}
                    </span>
                  </div>
                </div>
                <div className="vendor-arrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Accounts;
